'use client';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import Axios from '@/lib/axiosConfig';
import { useAuth } from '@/context/Auth';
import { toast } from 'sonner';

export interface EducationWishlistRequest {
  educationID: string;
}

export interface EducationWishlistResponse {
  success: boolean;
  message: string | null;
  responseObject: any | null;
  statusCode: number;
}

export const useEducationWishlistMutation = () => {
  const { getAPIRequestTokenHeaders } = useAuth();
  const queryClient = useQueryClient();

  return useMutation<EducationWishlistResponse, Error, { educationID: string; isOnWishlist: boolean }>({
    mutationFn: ({ educationID, isOnWishlist }) => {
      if (isOnWishlist) {
        // Remove from wishlist (DELETE)
        return Axios.delete(`/educations/wishlist`, {
          headers: getAPIRequestTokenHeaders(),
          data: { educationID }, // DELETE with body
        }).then(res => {
          return res.data;
        }).catch(error => {
          throw error;
        });
      } else {
        // Add to wishlist (POST)
        return Axios.post('/educations/wishlist', { educationID }, {
          headers: getAPIRequestTokenHeaders(),
        }).then(res => {
          return res.data;
        }).catch(error => {
          throw error;
        });
      }
    },
    onSuccess: (data, variables) => {
      const { educationID, isOnWishlist } = variables;
      
      // Show success toast
      if (data.success) {
        if (isOnWishlist) {
          toast.success('Removed from saved schools!');
        } else {
          toast.success('Added to saved schools!');
        }
        
        // Invalidate all education queries to refresh the data
        queryClient.invalidateQueries({ queryKey: ['educations'] });
        queryClient.invalidateQueries({ queryKey: ['educations/my'] });
        queryClient.invalidateQueries({ queryKey: ['educations/wishlist'] });
        
        // Invalidate the specific education query if it exists
        queryClient.invalidateQueries({ queryKey: ['education', educationID] });
      } else {
        // Handle API error response
        const errorMessage = data.message || 'Failed to update saved schools';
        toast.error(errorMessage);
      }
    },
    onError: (error: any) => {
      // Show error toast with the specific error message
      const errorMessage = error?.response?.data?.message || 'Failed to update saved schools';
      toast.error(errorMessage);
    },
  });
};
